/*
 OK FBI — Case Media Backup (host-only)
 Per-case snapshot/restore. An auto safety-snapshot is taken before every restore
 (backend-enforced). Backups are metadata + a lightweight state slice — never exposed
 on OBS overlay.
*/
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Trash2, Archive, X, Plus, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("ar-EG"); } catch { return iso; }
};

export function CaseBackupButton({ caseId, label = "نسخ احتياطية" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`open-case-backup-${caseId}`}
        className="text-[11px] font-cairo px-3 py-1.5 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1.5">
        <Archive size={12} /> {label}
      </button>
      <CaseBackupModal open={open} onClose={() => setOpen(false)} caseId={caseId} />
    </>
  );
}

export function CaseBackupModal({ open, onClose, caseId }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [confirmRestore, setConfirmRestore] = useState(null); // backup obj
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/session/case-backup/${caseId}`);
      setBackups(Array.isArray(data) ? data : []);
    } catch { setBackups([]); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/session/case-backup/${caseId}`, { label: newLabel || undefined });
      setNewLabel("");
      await load();
      toast.success("تم إنشاء النسخة الاحتياطية");
    } catch { toast.error("تعذّر إنشاء النسخة"); }
    finally { setBusy(false); }
  };
  const restore = async (bk) => {
    setBusy(true);
    try {
      await api.post(`/session/case-backup/${caseId}/${bk.backup_id}/restore`);
      await load();
      toast.success("تم الاستعادة · حُفظت نقطة أمان تلقائية");
    } catch { toast.error("تعذّر الاستعادة"); }
    finally { setBusy(false); setConfirmRestore(null); }
  };
  const del = async (bk) => {
    setBusy(true);
    try {
      await api.delete(`/session/case-backup/${bk.backup_id}`);
      await load();
      toast.success("تم الحذف");
    } catch { toast.error("تعذّر الحذف"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        dir="rtl" data-testid="case-backup-modal"
        className="fixed inset-0 z-[999] bg-black/95 text-white p-4 md:p-6 flex items-center justify-center">
        <div className="w-[720px] max-w-[95vw] max-h-[90vh] flex flex-col border border-metal bg-gradient-to-b from-[rgba(15,15,17,0.98)] to-[rgba(6,6,8,0.98)]"
          style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(197,160,89,0.08)" }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-metal">
            <div className="flex items-center gap-2">
              <Archive size={16} className="text-gold" />
              <div>
                <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold">CASE ARCHIVE</div>
                <div className="font-cairo text-lg">نسخ احتياطية للقضية</div>
              </div>
            </div>
            <button onClick={onClose} data-testid="case-backup-close"
              className="w-9 h-9 border border-metal text-zinc-400 hover:border-gold hover:text-gold flex items-center justify-center"><X size={16} /></button>
          </div>
          {/* Create */}
          <div className="p-4 border-b border-metal">
            <div className="text-[10px] font-mono-ar text-zinc-500 mb-1.5">إنشاء نسخة احتياطية جديدة</div>
            <div className="flex gap-2">
              <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                data-testid="case-backup-label-input" maxLength={120}
                placeholder="مسمى النسخة (اختياري) — مثلاً: قبل جولة التصويت"
                className="flex-1 bg-black/40 border border-metal px-3 py-2 text-[13px] text-white outline-none focus:border-gold font-cairo" />
              <button onClick={create} disabled={busy} data-testid="case-backup-create"
                className="btn-primary text-[12px] px-4 flex items-center gap-1.5">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} إنشاء نسخة
              </button>
            </div>
          </div>
          {/* List */}
          <div className="flex-1 overflow-auto" data-testid="case-backup-list">
            {loading && <div className="text-sm text-zinc-500 text-center pt-8">جارٍ التحميل…</div>}
            {!loading && backups.length === 0 && (
              <div className="text-sm text-zinc-500 text-center pt-8">لا توجد نسخ محفوظة لهذه القضية.</div>
            )}
            {backups.map(bk => (
              <div key={bk.backup_id} className="flex items-center gap-3 border-b border-metal/40 px-4 py-3 hover:bg-black/40"
                   data-testid={`case-backup-row-${bk.backup_id}`}>
                <div className={`w-8 h-8 border flex items-center justify-center shrink-0 ${bk.auto ? "border-emerald-800 text-emerald-400" : "border-gold text-gold"}`}>
                  {bk.auto ? <ShieldCheck size={13} /> : <Save size={13} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-cairo text-white truncate">{bk.label || "نسخة"}</div>
                  <div className="text-[10px] font-mono-ar text-zinc-500 flex items-center gap-2 mt-0.5">
                    <span>{fmtDate(bk.saved_at)}</span>
                    <span>·</span>
                    <span>{fmtBytes(bk.size_bytes)}</span>
                    {bk.auto && <><span>·</span><span className="text-emerald-400">تلقائية</span></>}
                  </div>
                </div>
                <button onClick={() => setConfirmRestore(bk)} disabled={busy}
                  data-testid={`case-backup-restore-${bk.backup_id}`}
                  className="text-[11px] font-cairo px-3 py-1 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-1"><RotateCcw size={11} /> استعادة</button>
                <button onClick={() => del(bk)} disabled={busy}
                  data-testid={`case-backup-delete-${bk.backup_id}`}
                  className="w-8 h-8 border border-red-900 text-red-400 hover:bg-red-950/30 flex items-center justify-center"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-metal text-[9px] text-zinc-600 font-mono-ar leading-tight">
            الاستعادة تُنشئ نقطة أمان تلقائية للحالة الحالية قبل التطبيق · لا تظهر النسخ الاحتياطية على البث.
          </div>
        </div>
        {/* Restore confirmation */}
        {confirmRestore && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="fixed inset-0 z-[1001] bg-black/85 flex items-center justify-center p-4"
            data-testid="case-backup-confirm">
            <div className="w-[440px] max-w-[95vw] border border-gold bg-[rgba(10,10,12,0.98)] p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert size={18} className="text-gold" />
                <div className="font-cairo text-lg">تأكيد الاستعادة</div>
              </div>
              <div className="text-[12px] text-zinc-300 mb-4 font-cairo leading-relaxed">
                ستُستبدل حالة القضية الحالية (الأدلة المكشوفة، الأصوات، الملاحظات، المؤقتات، النتائج) بحالة نسخة:
                <div className="mt-2 text-white">«{confirmRestore.label}»</div>
                <div className="mt-2 text-[10px] font-mono-ar text-zinc-500">{fmtDate(confirmRestore.saved_at)}</div>
                <div className="mt-3 text-emerald-400 text-[11px]">سيتم إنشاء نقطة أمان تلقائية قبل التطبيق.</div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmRestore(null)} data-testid="case-backup-confirm-cancel"
                  className="text-[12px] px-4 py-1.5 border border-metal text-zinc-300 hover:text-white">إلغاء</button>
                <button onClick={() => restore(confirmRestore)} disabled={busy}
                  data-testid="case-backup-confirm-yes"
                  className="text-[12px] px-4 py-1.5 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-1">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} استعادة الآن
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
